import React, { useState } from "react";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { showError } from "toast/toast";

/* ================= TYPES ================= */

type StripePaymentFormProps = {
  amount: number;
  onSuccess: () => void;
};

/* ================= COMPONENT ================= */

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({ amount, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  /* ================= HANDLE PAYMENT ================= */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      showError("Stripe is not ready. Please try again.");
      return;
    }

    setLoading(true);

    try {
      // Submit the PaymentElement
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      // Confirm the payment
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (error) {
        throw new Error(error.message);
      }

      // Payment successful
      if (paymentIntent?.status === "succeeded") {
        onSuccess();
      }
    } catch (err) {
      console.error("Payment failed:", err);
      showError(err instanceof Error ? err.message : "Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Stripe Payment Element */}
      <div className="mb-3">
        <PaymentElement
          options={{
            layout: {
              type: "tabs",
              defaultCollapsed: false,
            },
          }}
        />
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        className="btn btn-primary w-100  table-addbutton"
        disabled={!stripe || loading}
      >
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" />
            Processing...
          </>
        ) : (
          <>
            <i className="ri-add-line me-1" />
            Add ${amount.toFixed(2)} to Wallet
          </>
        )}
      </button>
    </form>
  );
};

export default StripePaymentForm;
